import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { format } from 'date-fns';

export interface ISignatureStampOptions {
  signerName: string;
  signerCpf?: string;
  signerRole?: string;
  certificateType: 'govbr_prata' | 'govbr_ouro' | 'icp_brasil_a1' | 'icp_brasil_a3' | 'eletronica';
  authCode: string;
  manifestPage?: boolean;
}

/**
 * Aplica a chancela visual de assinatura eletrônica (Gov.br / ICP-Brasil) no PDF
 */
export async function applySignatureToPdf(
  pdfBuffer: ArrayBuffer,
  options: ISignatureStampOptions
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.load(pdfBuffer, { ignoreEncryption: true });
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const now = new Date();
  const dateStr = format(now, "dd/MM/yyyy 'às' HH:mm:ss");
  const certLabel = options.certificateType.startsWith('govbr')
    ? `Assinatura Eletrônica Avançada Gov.br (${options.certificateType === 'govbr_ouro' ? 'Nível Ouro' : 'Nível Prata'})`
    : 'Assinatura Digital Qualificada (Padrão ICP-Brasil)';

  if (options.manifestPage) {
    // Anexa uma Folha de Manifesto Oficial ao final do documento
    const manifestPage = pdfDoc.addPage([595.28, 841.89]); // A4 padrão
    const { width, height } = manifestPage.getSize();

    // Faixa superior azul institucional
    manifestPage.drawRectangle({
      x: 0,
      y: height - 60,
      width: width,
      height: 60,
      color: options.certificateType.startsWith('govbr') ? rgb(0.0, 0.35, 0.58) : rgb(0.1, 0.45, 0.3),
    });

    manifestPage.drawText('GEDACERVO — MANIFESTO DE ASSINATURAS ELETRÔNICAS', {
      x: 35,
      y: height - 38,
      size: 14,
      font: fontBold,
      color: rgb(1, 1, 1),
    });

    // Subtítulo
    manifestPage.drawText('Termo de Conformidade com a Lei Federal nº 14.063/2020 e MP nº 2.200-2/2001', {
      x: 35,
      y: height - 85,
      size: 10,
      font: font,
      color: rgb(0.4, 0.4, 0.4),
    });

    // Caixa de Assinatura
    const boxY = height - 260;
    manifestPage.drawRectangle({
      x: 35,
      y: boxY,
      width: width - 70,
      height: 155,
      borderColor: rgb(0.8, 0.85, 0.9),
      borderWidth: 1,
      color: rgb(0.97, 0.98, 1.0),
    });

    manifestPage.drawText(`✓ ${certLabel}`, {
      x: 50,
      y: boxY + 130,
      size: 12,
      font: fontBold,
      color: options.certificateType.startsWith('govbr') ? rgb(0.0, 0.35, 0.58) : rgb(0.1, 0.45, 0.3),
    });

    manifestPage.drawText(`Signatário: ${options.signerName}`, {
      x: 50,
      y: boxY + 105,
      size: 11,
      font: fontBold,
      color: rgb(0.15, 0.15, 0.15),
    });

    manifestPage.drawText(`Identificação (CPF): ${options.signerCpf || '***.***.***-**'}`, {
      x: 50,
      y: boxY + 85,
      size: 10,
      font: font,
      color: rgb(0.3, 0.3, 0.3),
    });

    manifestPage.drawText(`Cargo / Função: ${options.signerRole || 'Servidor Público / Cidadão'}`, {
      x: 50,
      y: boxY + 65,
      size: 10,
      font: font,
      color: rgb(0.3, 0.3, 0.3),
    });

    manifestPage.drawText(`Data e Hora da Assinatura: ${dateStr} (Horário Oficial de Brasília)`, {
      x: 50,
      y: boxY + 45,
      size: 10,
      font: font,
      color: rgb(0.3, 0.3, 0.3),
    });

    manifestPage.drawText(`Código Verificador de Autenticidade: ${options.authCode}`, {
      x: 50,
      y: boxY + 25,
      size: 10,
      font: fontBold,
      color: rgb(0.0, 0.35, 0.58),
    });

    // Rodapé de Validação
    manifestPage.drawText(
      'A autenticidade deste documento pode ser verificada no sistema GEDAcervo mediante leitura do QR Code ou',
      {
        x: 35,
        y: 60,
        size: 9,
        font: font,
        color: rgb(0.45, 0.45, 0.45),
      }
    );
    manifestPage.drawText(
      `consulta pública utilizando o código identificador único: ${options.authCode}`,
      {
        x: 35,
        y: 45,
        size: 9,
        font: fontBold,
        color: rgb(0.2, 0.2, 0.2),
      }
    );

  } else {
    // Carimbo visual no rodapé da última página
    const pages = pdfDoc.getPages();
    const targetPage = pages[pages.length - 1];
    const { width } = targetPage.getSize();

    const stampHeight = 36;
    const stampWidth = width - 40;
    const stampX = 20;
    const stampY = 15;

    // Fundo do carimbo
    targetPage.drawRectangle({
      x: stampX,
      y: stampY,
      width: stampWidth,
      height: stampHeight,
      color: rgb(0.96, 0.98, 1.0),
      borderColor: rgb(0.2, 0.45, 0.7),
      borderWidth: 0.8,
      opacity: 0.95,
    });

    targetPage.drawText(
      `[ASSINADO ELETRONICAMENTE] ${options.signerName} - ${certLabel} em ${dateStr}`,
      {
        x: stampX + 8,
        y: stampY + 20,
        size: 7.5,
        font: fontBold,
        color: rgb(0.0, 0.3, 0.55),
      }
    );

    targetPage.drawText(
      `Autenticidade verificável via portal GEDAcervo | Código: ${options.authCode} | Dec. nº 10.543/2020 e Lei 14.063/2020`,
      {
        x: stampX + 8,
        y: stampY + 8,
        size: 6.5,
        font: font,
        color: rgb(0.3, 0.3, 0.3),
      }
    );
  }

  return await pdfDoc.save();
}
